import { useCallback, useEffect, useState } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  Users,
  BadgePoundSterling,
  BarChart3,
  Layers,
  Circle,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

const TIER_LABEL = { foundation: 'Foundation', practitioner: 'Practitioner', advanced: 'Advanced' };
const STATUS = {
  active:     { fg: 'var(--ok)',     bg: 'var(--ok-10)',     label: 'Active',     pulse: true },
  instalment: { fg: 'var(--info)',   bg: 'var(--info-10)',   label: 'Instalment', pulse: false },
  completed:  { fg: 'var(--ts)',     bg: 'var(--neutral-10)', label: 'Completed',  pulse: false },
  cancelled:  { fg: 'var(--danger)', bg: 'var(--danger-10)', label: 'Cancelled',  pulse: false },
};

const formatGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n);

function formatRelative(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `Today, ${hh}:${mm}`;
  }
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays >= 1 && diffDays <= 30) return `${diffDays} days ago`;
  return d.toLocaleDateString('en-GB');
}

function TierBadge({ tier }) {
  return (
    <span
      className="inline-flex items-center font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded"
      style={{
        background: `var(--tier-${tier}-bg)`,
        color: `var(--tier-${tier}-fg)`,
      }}
    >
      {TIER_LABEL[tier] || tier}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.active;
  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded"
      style={{ background: s.bg, color: s.fg }}
    >
      <Circle
        size={8}
        fill={s.fg}
        style={{
          color: s.fg,
          animation: s.pulse ? 'pulseDot 2s ease-in-out infinite' : 'none',
        }}
      />
      {s.label}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, testId }) {
  return (
    <div data-testid={testId} className="p-5 rounded-lg" style={CARD_STYLE}>
      <div className="flex items-start justify-between mb-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
          {label}
        </p>
        <div
          className="flex items-center justify-center rounded"
          style={{ width: 28, height: 28, background: 'var(--a-glow)', border: '1px solid var(--a-border)' }}
        >
          <Icon size={14} style={{ color: 'var(--a)' }} />
        </div>
      </div>
      <p className="font-mono text-3xl font-semibold" style={{ color: 'var(--white)' }}>
        {value}
      </p>
    </div>
  );
}

function PaymentMethod({ method, n, total }) {
  if (method === 'instalment') {
    return (
      <span className="font-mono text-xs" style={{ color: 'var(--info)' }}>
        Instalment {n}/{total}
      </span>
    );
  }
  return (
    <span className="font-mono text-xs" style={{ color: 'var(--tm)' }}>Full</span>
  );
}

export default function EnrolmentsSection() {
  const { adminFetch } = useAdminAuth();
  const [enrolments, setEnrolments] = useState([]);
  const [summary, setSummary] = useState({ active: 0, total_revenue: 0, average_order_value: 0, on_instalments: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/enrolments`);
      if (!res.ok) throw new Error(`Load failed: ${res.status}`);
      const body = await res.json();
      setEnrolments(body.enrolments);
      setSummary(body.summary);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-enrolments-section">
      {/* Header */}
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
          section · enrolments
        </p>
        <h2 className="font-display text-2xl font-bold mt-1" style={{ color: 'var(--white)' }}>
          Enrolments
        </h2>
      </div>

      {error && (
        <div className="p-3 rounded" style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}>
          <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{error}</p>
        </div>
      )}

      {/* 4 stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-enrolments-summary">
        <StatCard
          label="Active Enrolments"
          value={new Intl.NumberFormat('en-GB').format(summary.active)}
          icon={Users}
          testId="admin-enrolments-stat-active"
        />
        <StatCard
          label="Total Revenue"
          value={formatGBP(summary.total_revenue)}
          icon={BadgePoundSterling}
          testId="admin-enrolments-stat-revenue"
        />
        <StatCard
          label="Average Order Value"
          value={formatGBP(summary.average_order_value)}
          icon={BarChart3}
          testId="admin-enrolments-stat-aov"
        />
        <StatCard
          label="On Instalments"
          value={new Intl.NumberFormat('en-GB').format(summary.on_instalments)}
          icon={Layers}
          testId="admin-enrolments-stat-instalments"
        />
      </div>

      {/* Table */}
      <div className="rounded-lg overflow-hidden" style={CARD_STYLE} data-testid="admin-enrolments-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: 'var(--l2)', borderBottom: '1px solid var(--b1)' }}>
                <Th>Student</Th>
                <Th>Course</Th>
                <Th>Tier</Th>
                <Th align="right">Amount Paid</Th>
                <Th>Payment Method</Th>
                <Th>Enrolled</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-6 text-center font-mono text-xs" style={{ color: 'var(--a)' }}>Loading…</td></tr>
              )}
              {!loading && enrolments.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center font-body text-sm" style={{ color: 'var(--te)' }}>
                  No enrolments yet.
                </td></tr>
              )}
              {enrolments.map((e, idx) => (
                <tr
                  key={e.id}
                  data-testid={`admin-enrolment-row-${e.id}`}
                  style={{ borderBottom: idx === enrolments.length - 1 ? 'none' : '1px solid var(--b1)' }}
                >
                  <Td>
                    <p className="font-body text-sm font-medium" style={{ color: 'var(--white)' }}>{e.student_name}</p>
                    {e.student_email && (
                      <p className="font-mono text-[11px] mt-0.5" style={{ color: 'var(--ts)' }}>{e.student_email}</p>
                    )}
                  </Td>
                  <Td>
                    <span className="font-body text-sm" style={{ color: 'var(--tm)' }}>{e.course_name}</span>
                  </Td>
                  <Td><TierBadge tier={e.tier} /></Td>
                  <Td align="right">
                    <span className="font-mono text-sm" style={{ color: 'var(--white)' }}>{formatGBP(e.amount_paid)}</span>
                  </Td>
                  <Td>
                    <PaymentMethod method={e.payment_method} n={e.instalment_number} total={e.instalment_total} />
                  </Td>
                  <Td>
                    <span className="font-mono text-xs" style={{ color: 'var(--tm)' }}>{formatRelative(e.enrolled_at)}</span>
                  </Td>
                  <Td><StatusBadge status={e.status} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Th({ children, align = 'left' }) {
  return (
    <th
      className="font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-3"
      style={{ color: 'var(--ts)', textAlign: align }}
    >
      {children}
    </th>
  );
}
function Td({ children, align = 'left' }) {
  return <td className="px-4 py-3" style={{ textAlign: align, verticalAlign: 'top' }}>{children}</td>;
}
